import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = getArticleBySlug(slug || '');
  const recentArticles = getRecentArticles(4).filter((a) => a.slug !== slug);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="您访问的文章不存在或已被删除。" />
        <Navbar />
        <main className="pt-20 pb-14 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '爱爱网',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`${article.categoryName},爱爱网,情感文章,两性知识`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
        articleAuthor={article.author}
      />
      <Navbar />
      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: article.categoryName, path: `/category/${article.category}` },
              { name: article.title },
            ]}
          />

          <article className="mt-8">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>

              <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-6">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-3 py-4 border-y border-background-200/60">
                <img
                  src={article.authorImage}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="text-sm font-medium text-foreground-900">{article.author}</div>
                  <div className="text-xs text-foreground-400">{article.authorTitle}</div>
                </div>
              </div>
            </div>

            <div className="relative h-[400px] md:h-[500px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="article-content prose prose-base max-w-none text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {recentArticles.length > 0 && (
            <section className="mt-16 pt-12 border-t border-background-200/60">
              <h3 className="text-xl font-semibold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                推荐文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {recentArticles.slice(0, 4).map((a) => (
                  <ArticleCard key={a.id} {...a} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}